import { useRef, useState } from 'react';

import { Box, Center, Image } from '@chakra-ui/react';
import { ChevronLeft, ChevronRight, PhotoSizeSelectActual } from '@m/icons';

import { Badge } from '@m/components-ui/badge';
import { Button } from '@m/components-ui/button';

import defaultZonePhotoOne from './default-zone-photo-1.jpg';
import defaultZonePhotoTwo from './default-zone-photo-2.jpg';
import listZonePlaceholder from './list-zone-placeholder.jpg';

/** Customer and marketing heroes show only the first photos in manager order. */
export const HERO_CAROUSEL_LIMIT = 4;

type BundledPhoto = string | { src: string };

function bundledPhotoSrc(photo: BundledPhoto): string {
  return typeof photo === 'string' ? photo : photo.src;
}

/** Garage and rooftop photos used when a hero has no API photos. */
export const DEFAULT_ZONE_PHOTO_URLS = [
  bundledPhotoSrc(defaultZonePhotoOne),
  bundledPhotoSrc(defaultZonePhotoTwo),
];

/** List cards use this mark when a location has no API photo. Heroes do not. */
export const LIST_ZONE_PLACEHOLDER_URL = bundledPhotoSrc(listZonePlaceholder);

/** API photos for the map preview, capped at four. No placeholder and no defaults. */
export function previewImageUrls(
  imageUrls?: readonly string[] | null,
  imageUrl?: string,
): string[] {
  const ordered = (imageUrls ?? []).map((url) => url.trim()).filter((url) => url !== '');
  if (ordered.length > 0) return ordered.slice(0, HERO_CAROUSEL_LIMIT);
  const single = imageUrl?.trim();
  return single ? [single] : [];
}

/** First API photo for a list card, or undefined so the card shows the mark. */
export function listCardImageUrl(
  imageUrls?: readonly string[] | null,
  imageUrl?: string,
): string | undefined {
  const ordered = (imageUrls ?? []).map((url) => url.trim()).filter((url) => url !== '');
  if (ordered[0]) return ordered[0];
  const single = imageUrl?.trim();
  return single || undefined;
}

/**
 * Prefer an ordered list. One photo stays a static hero. Two or more become a
 * carousel, capped at four. No API photos use the two default placeholders.
 */
export function heroCarouselUrls(
  imageUrls?: readonly string[] | null,
  imageUrl?: string,
): string[] {
  const ordered = (imageUrls ?? []).map((url) => url.trim()).filter((url) => url !== '');
  if (ordered.length > 0) {
    return ordered.slice(0, HERO_CAROUSEL_LIMIT);
  }
  const single = imageUrl?.trim();
  return single ? [single] : [...DEFAULT_ZONE_PHOTO_URLS];
}

type HeroImageCarouselProps = {
  alt: string;
  urls: readonly string[];
};

export function HeroImageCarousel({ alt, urls }: HeroImageCarouselProps) {
  const photos = urls.slice(0, HERO_CAROUSEL_LIMIT);
  const photoKey = photos.join('\0');
  const [index, setIndex] = useState(0);
  const [renderedPhotoKey, setRenderedPhotoKey] = useState(photoKey);
  const touchStartX = useRef<number | null>(null);

  if (photoKey !== renderedPhotoKey) {
    setRenderedPhotoKey(photoKey);
    setIndex(0);
  }

  const count = photos.length;
  const safeIndex = photoKey !== renderedPhotoKey || count === 0 ? 0 : index % count;

  if (count === 0) {
    return (
      <Center w="full" h="full" bg="fill.background.light-gray" color="text.secondary">
        <PhotoSizeSelectActual />
      </Center>
    );
  }

  const showControls = count > 1;
  const current = photos[safeIndex] ?? photos[0];

  const step = (delta: number) => {
    setIndex((currentIndex) => (currentIndex + delta + count) % count);
  };

  return (
    <Box
      position="relative"
      w="full"
      h="full"
      onTouchStart={(event) => {
        touchStartX.current = event.changedTouches[0]?.clientX ?? null;
      }}
      onTouchEnd={(event) => {
        const startX = touchStartX.current;
        const endX = event.changedTouches[0]?.clientX;
        touchStartX.current = null;
        if (startX == null || endX == null || !showControls) return;
        const delta = endX - startX;
        if (delta > 40) step(-1);
        else if (delta < -40) step(1);
      }}
    >
      <Image
        src={current}
        alt={alt}
        w="full"
        h="full"
        objectFit="cover"
        aria-roledescription={showControls ? 'slide' : undefined}
      />
      {showControls && (
        <>
          <Button
            aria-label="Previous photo"
            position="absolute"
            top="50%"
            left="4"
            transform="translateY(-50%)"
            hideBelow="md"
            size="xs"
            variant="secondary"
            w="28px"
            minW="28px"
            h="28px"
            px="0"
            borderRadius="full"
            bg="blackAlpha.700"
            color="text.white"
            _hover={{ bg: 'blackAlpha.800' }}
            onClick={(event) => {
              event.stopPropagation();
              step(-1);
            }}
          >
            <ChevronLeft />
          </Button>
          <Button
            aria-label="Next photo"
            position="absolute"
            top="50%"
            right="4"
            transform="translateY(-50%)"
            hideBelow="md"
            size="xs"
            variant="secondary"
            w="28px"
            minW="28px"
            h="28px"
            px="0"
            borderRadius="full"
            bg="blackAlpha.700"
            color="text.white"
            _hover={{ bg: 'blackAlpha.800' }}
            onClick={(event) => {
              event.stopPropagation();
              step(1);
            }}
          >
            <ChevronRight />
          </Button>
          <Badge
            variant="neutral"
            size="sm"
            position="absolute"
            bottom="4"
            left="50%"
            transform="translateX(-50%)"
            pointerEvents="none"
            color="text.white"
            fontWeight="500"
            fontSize={{ base: '12px', md: '14px' }}
            lineHeight={{ base: '18px', md: '20px' }}
          >
            {safeIndex + 1}/{count}
          </Badge>
        </>
      )}
    </Box>
  );
}
